import {AppUrls, ROBOKASSA_PAYMENT_PAGE} from 'shared/config/model/urls';

import React, {useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import cn from 'classnames';
import {toast} from 'react-toastify';

import {paymentAPI} from 'entities/payment';

import Checkbox from 'shared/ui/Checkbox/Checkbox';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import Button from 'shared/ui/Button/Button/Button';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import RadioGroup from 'shared/ui/RadioGroup/RadioGroup';
import InputText from 'shared/ui/Input/InputText';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import InputEmail from 'shared/ui/Input/InputEmail';
import {ITariff} from 'entities/tariff';
import {absoluteIri} from 'shared/api/iriHelper';
import {reachGoal, YM_GOALS} from 'shared/lib/analytics';

import {t} from '@lingui/core/macro';

import {isTranslate} from '../../../../../shared/lib/lang';

import styles from './TariffForm.module.scss';

interface ITariffFormProps{
    onSuccess: () => void,
    onLegalSuccess?: () => void,
    item ?: ITariff,
    userCurrentTariff?: {
        id: number,
        name: string;
        is_archive?: boolean
        price?: number
    }
}

interface ICreatePaymentForm{
    recurring: boolean,
}

interface ILegalEntitiesForm {
    edo_system: string;
    edo_id_or_inn: string;
    invoice_email: string;
}

type FormData = ICreatePaymentForm & Partial<ILegalEntitiesForm>;

const TariffForm: React.FC<ITariffFormProps> = ({
    item,
    userCurrentTariff,
    onSuccess,
    onLegalSuccess,
}) => {
    const [selectedTariff, setSelectedTariff] = useState(item);

    const [isRecurringChecked, setIsRecurringChecked] = useState(false);
    const [isForLegalEntities, setIsForLegalEntities] = useState(false);
    const {register, handleSubmit, watch, control, setValue} = useForm<FormData>({mode: 'onChange'});
    const isRenewal = Boolean(userCurrentTariff && item?.id === userCurrentTariff.id);
    const recurringValue = watch('recurring');
    const isButtonDisabled = isRecurringChecked && !recurringValue;

    const edoSystem = watch('edo_system');
    const edoIdOrInn = watch('edo_id_or_inn');
    const invoiceEmail = watch('invoice_email');

    const areLegalFieldsFilled = Boolean(edoSystem && edoIdOrInn && invoiceEmail);

    const isSubmitDisabled = isForLegalEntities
        ? !areLegalFieldsFilled
        : isButtonDisabled;
    
    const [addPayment, {isLoading: isLoadingCreate}] = paymentAPI.useAddPaymentMutation();
    const [addLegalInvoice] = paymentAPI.useAddLegalInvoiceMutation();

    useEffect(() => {
        if (isForLegalEntities) {
            setIsRecurringChecked(false);
            setValue('recurring', false);
        }
    }, [isForLegalEntities, setValue]);

    const handleLegalEntitiesToggle = () => {
        setIsForLegalEntities(prev => !prev);
    };
    const handleRecurringToggle = () => {
        setIsRecurringChecked(prev => !prev);
    };

    const onSubmit: SubmitHandler<FormData> = async(data) => {
        if (isForLegalEntities) {
            const legalPayload = {
                edo_system: data.edo_system || '',
                edo_id_or_inn: data.edo_id_or_inn || '',
                tariff_name: `${selectedTariff?.name}${selectedTariff?.is_archive ? ` (${t`архивный`})` : ''}`,
                tariff_duration: `${selectedTariff?.period} ${t`дней`}`,
                tariff_price: `${selectedTariff?.price}`,
                invoice_email: data.invoice_email || '',
            };
            await addLegalInvoice(legalPayload).unwrap();

            toast.info(`Мы получили вашу заявку на оплату
                и уже начали её обработку. Счёт будет отправлен
                на ${data.invoice_email} в ближайшее время.`);

            reachGoal(YM_GOALS.SUBMIT_FORM_LEGAL_PAYMENT)
            onSuccess();
            if (onLegalSuccess) {
                onLegalSuccess();
            }

        } else {
            const selectedVariant = selectedTariff?.variants?.find(
                v => v.period === selectedTariff.period,
            );

            const payment = await addPayment({
                tariff: absoluteIri('tariffs', selectedTariff?.id ?? 0),
                variant: selectedVariant?.id
                    ? absoluteIri('tariff-variants', selectedVariant.id)
                    : null,
                recurring: data.recurring,
            }).unwrap();

            reachGoal(
                isRenewal
                    ? YM_GOALS.TARIFF_RENEWAL_PAYMENT_START
                    : YM_GOALS.START_NEW_TARIFF_PAYMENT,
            );

            if (payment?.invoice_id) {
                window.location.href = ROBOKASSA_PAYMENT_PAGE(payment.invoice_id);
            }
        }
    };

    const getButtonText = () => {
        if (isForLegalEntities) {
            return t`Запросить счёт`;
        }

        if (isRenewal) {
            return t`Продлить тариф`;
        }

        return t`Оплатить`;
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className={cn(styles.form, {
                [styles.langEn]: isTranslate(),
            })}
        >
            <ModalBodyWrapper>
                {userCurrentTariff?.is_archive && !item?.is_archive && userCurrentTariff?.price !== 0 && (
                    <div className={styles.archiveWarning}>
                        {t`Ваш архивный тариф`} «{userCurrentTariff?.name}» {' '}
                        {/* eslint-disable-next-line max-len */}
                        {t`пропадет при смене на любой другой тариф, в том числе на бесплатный, если окончится оплаченный период`}.
                    </div>
                )}

                <div className={styles.countMonitorsBlock}>
                    <span className={styles.title}>
                        {t`Выберите период тарифа`}
                    </span>
                    <div className={styles.action}>
                        {selectedTariff?.variants?.map((variant, index) => (
                            <div
                                key={index}
                                className={cn(
                                    styles.item,
                                    selectedTariff?.period === variant.period ? styles.active : '',
                                    styles.itemWithSale,
                                )}
                                onClick={() => {
                                    if (selectedTariff) {
                                        setSelectedTariff({
                                            ...selectedTariff,
                                            period: variant.period,
                                            price: variant.price,
                                        });
                                    }
                                }}
                            >
                                {variant.period} {t`дней`}
                                {[90, 180, 365].includes(variant.period) && (
                                    <span className={styles.sale}>-{variant.discount}%</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                {
                    !isTranslate() &&
                    <div className={styles.toggle}>
                        <StatusToggle
                            condition={isForLegalEntities}
                            onClick={handleLegalEntitiesToggle}
                            textActive="Оплата для юр. лиц"
                            notActiveText="Оплата для юр. лиц"
                        />
                    </div>
                }

                {isForLegalEntities
                    ?
                    <div className={styles.legalEntitiesForm}>
                        <FormGroup className={styles.formGroup}>
                            <label className={styles.label}>Выберите систему ЭДО</label>
                            <RadioGroup
                                name="edo_system"
                                options={[
                                    {
                                        value: 'kontur-diadok',
                                        label: 'Контур.Диадок',
                                    },
                                    {
                                        value: 'sbis',
                                        label: 'СБИС',
                                    },
                                    {
                                        value: 'kaluga-astral',
                                        label: 'Калуга Астрал',
                                    },
                                ]}
                                control={control}
                                required={true}
                            />
                        </FormGroup>
                        <FormGroup className={styles.formGroup}>
                            <label className={styles.label}>Укажите ИНН или ID в системе ЭДО</label>
                            <InputText
                                name="edo_id_or_inn"
                                register={register}
                                control={control}
                                validate={false}
                                required={true}
                            />
                        </FormGroup>
                        <FormGroup className={styles.formGroup}>
                            <label className={styles.label}>Укажите E-mail для получения счета</label>
                            <InputEmail
                                name="invoice_email"
                                register={register}
                                control={control}
                                required={true}
                            />
                        </FormGroup>
                        <span className={styles.text}>
                                Отправляя форму,&nbsp;я даю&nbsp;
                            <Link
                                target="_blank"
                                to={AppUrls.CONSENT}
                            >
                                согласие
                            </Link> на обработку моих персональных
                                данных согласно соответствующей&nbsp;
                            <Link
                                target="_blank"
                                to={AppUrls.PRIVACY}
                            >
                                политике
                            </Link>
                        </span>
                    </div>

                    : !isTranslate() &&
                    <div className={styles.containerRecurring}>
                        <StatusToggle
                            condition={isRecurringChecked}
                            onClick={handleRecurringToggle}
                            textActive={t`Автоплатёж включен`}
                            notActiveText={t`Включить автоплатёж`}
                        />
                        <HintQuestion className={styles.hint} placement="top-start">
                            <span>

                                Оплата продлевается автоматически в день окончания тарифа — чтобы мониторинг не
                                остановился и вы не пропустили инциденты. Автопродление можно отключить в разделе&nbsp;
                                <a href={AppUrls.PAYMENTS} target="_blank" rel="noreferrer">Платежи</a>.
                            </span>
                        </HintQuestion>
                    </div>
                }

                {
                    isRecurringChecked && !isForLegalEntities && !isTranslate() && (
                        <div className={styles.containerAccent}>
                            <span className={styles.accent}>
                                Внимание! Автоплатежи доступны только при оплате банковской
                                картой, Яндекс Pay и Mir Pay
                            </span>
                            
                            <Checkbox
                                id="recurring"
                                {...register('recurring')}
                                required={true}
                                className={styles.checkbox}
                            >
                                    Я даю согласие на регулярные списания,
                                    на обработку персональных данных и принимаю
                                    условия&nbsp;
                                <Link
                                    target="_blank"
                                    to={AppUrls.PAYMENT_OFFER}
                                >
                                        публичной оферты
                                </Link>
                            </Checkbox>
                        </div>)
                }

                <div className={styles.row}>
                    <div className={styles.label}>
                        {t`Название тарифа`}:
                    </div>
                    <div className={styles.value}>
                        {selectedTariff?.name}{selectedTariff?.is_archive && ' (архивный)'}
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.label}>
                        {t`Срок действия`}:
                    </div>
                    <div className={styles.value}>
                        {selectedTariff?.period} {t`дней`}
                    </div>
                </div>
                <div className={styles.divider}/>
                <div className={styles.row}>
                    <div className={styles.label}>
                        {t`Стоимость`}{selectedTariff?.period !== 30 ? t` со скидкой` : ''}:
                    </div>
                    <div className={styles.value}>
                        {selectedTariff?.price} {t`руб`}
                    </div>
                </div>
                {selectedTariff?.variants && selectedTariff.period !== 30 &&
                    selectedTariff.variants.find(v => v.period === selectedTariff.period)?.saving && (
                    <div className={styles.row}>
                        <div className={styles.label}>
                            {t`Скидка`}:
                        </div>
                        <div className={styles.value}>
                            {selectedTariff.variants.find(v => v.period === selectedTariff.period)?.saving} {t`руб`}
                        </div>
                    </div>
                )}
            </ModalBodyWrapper>

            <FormGroup>
                <Button
                    type="submit"
                    loading={isLoadingCreate}
                    size="lg"
                    block
                    disabled={isSubmitDisabled}
                >
                    {getButtonText()}
                </Button>
            </FormGroup>
        </form>
    );
};

export default TariffForm;